/**
 *
 * Provides utilities for translating builderProps into the legacy question "context" shape
 * expected by downstream symtools from zycommon-web (scenarios and keyboards).
 *
 */

import { physicalQuantities, unitPrefixes } from 'zycommon-web/utils/engineering-symbols';
import { buildEditorAtomicSymbols } from 'zycommon-web/utils/symbolic-tools/math-live-keyboard-shared';

import { boolFromUnknown } from '@/lib/primitive-utils';

import type { BuilderPropertyRecord } from '../tool-settings';
import { resolveNotation, resolveRequireUnits } from '../tool-settings/lib/builder-value-utils';
import { builderPropsToScenarioContext } from './mathlive-scenarios';

export { resolveNotation, resolveRequireUnits };

export type EditorAtomicSymbol = {
    key: string;
    latex: string;
    macro: string;
};

export type KeyboardQuestionContext = ReturnType<typeof builderPropsToScenarioContext> & {
    expectedString: string;
    physical_quantities: string[];
    physicalQuantitiesInVirtualKeyboard: Array<{ abbreviation: string; command: string; key?: string }>;
    unit_prefixes: string[];
    unitPrefixesInVirtualKeyboard: Array<{ abbreviation: string; command: string; exponent?: number }>;
    boolean_functions: string[];
    boolean_variables: string[];
    operators: string[];
    untyped_variables: string[];
    functions_variables: string;
    hide_E_notation: boolean;
    support_true_and_false: boolean;
    virtual_keyboard_layout_name: string;
    editor_atomic_symbols: Array<{ key: string; latex: string }>;
};

function splitCsv(value: unknown): string[] {
    if (typeof value !== 'string' || value === '') {
        return [];
    }
    return value.split(',').filter(Boolean);
}

function parseEditorAtomicSymbols(value: unknown): Array<{ key: string; latex: string }> {
    if (Array.isArray(value)) {
        return value.filter(
            (item): item is { key: string; latex: string } =>
                typeof item === 'object' &&
                item != null &&
                typeof (item as { key?: unknown }).key === 'string' &&
                typeof (item as { latex?: unknown }).latex === 'string',
        );
    }
    if (typeof value !== 'string' || value.trim() === '') {
        return [];
    }
    try {
        const parsed: unknown = JSON.parse(value);
        return parseEditorAtomicSymbols(parsed);
    } catch {
        return [];
    }
}

export function buildEditorAtomicSymbolsForShortAnswer(
    builder: BuilderPropertyRecord,
    latexToMacroMap: Record<string, EditorAtomicSymbol>,
): EditorAtomicSymbol[] {
    const parsed = parseEditorAtomicSymbols(builder.editor_atomic_symbols);
    return buildEditorAtomicSymbols(parsed, latexToMacroMap).symbols;
}

export function builderPropsToKeyboardContext(
    builder: BuilderPropertyRecord,
    { expectedString = '' }: { expectedString?: string } = {},
): KeyboardQuestionContext {
    const base = builderPropsToScenarioContext(builder);
    const physicalQuantityAbbreviations = splitCsv(builder.physical_quantities);
    const unitPrefixAbbreviations = splitCsv(builder.unit_prefixes);

    return {
        ...base,
        expectedString,
        physical_quantities: physicalQuantityAbbreviations,
        physicalQuantitiesInVirtualKeyboard: physicalQuantityAbbreviations
            .map(abbreviation => physicalQuantities.find(quantity => quantity.abbreviation === abbreviation))
            .filter((quantity): quantity is (typeof physicalQuantities)[number] => quantity != null),
        unit_prefixes: unitPrefixAbbreviations,
        unitPrefixesInVirtualKeyboard: unitPrefixAbbreviations
            .map(prefix => unitPrefixes.find(unitPrefix => unitPrefix.abbreviation === prefix))
            .filter((prefix): prefix is (typeof unitPrefixes)[number] => prefix != null),
        boolean_functions: splitCsv(builder.boolean_functions),
        boolean_variables: splitCsv(builder.boolean_variables),
        operators: splitCsv(builder.operators),
        untyped_variables: splitCsv(builder.untyped_variables),
        functions_variables: typeof builder.functions_variables === 'string' ? builder.functions_variables : '',
        hide_E_notation: boolFromUnknown(builder.hide_E_notation),
        support_true_and_false: boolFromUnknown(builder.support_true_and_false),
        virtual_keyboard_layout_name: typeof builder.virtual_keyboard_layout_name === 'string' ? builder.virtual_keyboard_layout_name : '',
        editor_atomic_symbols: parseEditorAtomicSymbols(builder.editor_atomic_symbols),
    };
}
